import React, { useState } from "react";
import {
  TextField,
  Button,
  Checkbox,
  FormControlLabel,
  Typography,
  Box,
  InputAdornment,
  IconButton,
  CircularProgress,
} from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import LockIcon from "@mui/icons-material/Lock";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import Logo from "../assets/srifin_final.svg";

const { API_CONFIG, REFRESH_CONFIG } = require("../configuration");

const NAVY = "#0B2545";
const SLATE = "#134074";
const PANEL_BLUE_START = "#1B4F8C";
const PANEL_BLUE_END = "#2E86C1";
const GOLD = "#C89B3C";
const BG = "#F7F9FB";

const Login = () => {
  const [emp_id, setemp_id] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage("");

    try {
      const response = await fetch(`${API_CONFIG.APIURL}/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ emp_id, password, rememberMe }),
      });

      const data = await response.json();

      if (response.ok) {
        localStorage.setItem("token", data.token);
        localStorage.setItem("isLoggedIn", "true");
        localStorage.setItem("loginTime", Date.now());
        localStorage.setItem("user", JSON.stringify(data.user || {}));
        localStorage.setItem("allowedModules", JSON.stringify(data.allowedModules || []));
        localStorage.setItem("branchesAssigned", JSON.stringify(data.branchesAssigned || []));

        setMessage("Login successful");

        if (data.allowedModules.includes("Admin")) {
          navigate("/admin-dashboard");
        } else {
          navigate("/DashBoard");
        }
      } else {
        setMessage(data.message || "Login failed");
      }
    } catch (error) {
      console.error("Login error:", error);
      setMessage(error.message || "Error connecting to the server");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        background: `radial-gradient(ellipse at 30% 20%, #163A63 0%, ${NAVY} 55%, #071B33 100%)`,
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: { xs: "16px", md: "40px" },
        fontFamily: "'Inter', 'Segoe UI', Roboto, sans-serif",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='400'%3E%3Cg stroke='rgba(255,255,255,0.22)' stroke-width='2.2' fill='none' stroke-linecap='round' stroke-linejoin='round'%3E%3Cg transform='translate(28,36) rotate(-8)'%3E%3Crect x='0' y='0' width='46' height='30' rx='2'/%3E%3Crect x='-6' y='30' width='58' height='6' rx='2'/%3E%3C/g%3E%3Cg transform='translate(230,50) rotate(14)'%3E%3Crect x='0' y='0' width='24' height='42' rx='4'/%3E%3Cline x1='9' y1='36' x2='15' y2='36'/%3E%3C/g%3E%3Cg transform='translate(70,190) rotate(-15)' stroke='rgba(200,155,60,0.38)'%3E%3Crect x='0' y='0' width='54' height='34' rx='5'/%3E%3Cline x1='0' y1='12' x2='54' y2='12'/%3E%3Cline x1='8' y1='24' x2='26' y2='24'/%3E%3C/g%3E%3Cg transform='translate(270,230)'%3E%3Cpath d='M0,20 A20,20 0 0,1 40,20'/%3E%3Crect x='-4' y='16' width='8' height='14' rx='3'/%3E%3Crect x='36' y='16' width='8' height='14' rx='3'/%3E%3C/g%3E%3Cg transform='translate(140,300) rotate(6)'%3E%3Crect x='0' y='8' width='48' height='32' rx='3'/%3E%3Cpath d='M16,8 v-6 a4,4 0 0 1 4,-4 h8 a4,4 0 0 1 4,4 v6'/%3E%3Cline x1='0' y1='22' x2='48' y2='22'/%3E%3C/g%3E%3Cg transform='translate(300,120) rotate(-10)'%3E%3Crect x='0' y='6' width='28' height='38' rx='4'/%3E%3Ccircle cx='14' cy='18' r='5'/%3E%3Cline x1='6' y1='32' x2='22' y2='32'/%3E%3Cline x1='8' y1='38' x2='20' y2='38'/%3E%3C/g%3E%3Cg transform='translate(20,270) rotate(10)' stroke='rgba(200,155,60,0.30)'%3E%3Crect x='0' y='0' width='24' height='42' rx='4'/%3E%3Cline x1='9' y1='36' x2='15' y2='36'/%3E%3C/g%3E%3Cg transform='translate(340,320) rotate(-6)'%3E%3Crect x='0' y='0' width='46' height='30' rx='2'/%3E%3Crect x='-6' y='30' width='58' height='6' rx='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E\")",
          backgroundRepeat: "repeat",
          backgroundSize: "340px 340px",
          pointerEvents: "none",
        }}
      />
      <Box
        sx={{
          position: "absolute",
          top: "-180px",
          left: "-140px",
          width: "480px",
          height: "480px",
          borderRadius: "50%",
          background: `radial-gradient(circle, ${GOLD}33 0%, transparent 70%)`,
          pointerEvents: "none",
        }}
      />
      <Box
        sx={{
          position: "absolute",
          bottom: "-220px",
          right: "-160px",
          width: "540px",
          height: "540px",
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(255,255,255,0.10) 0%, transparent 70%)",
          pointerEvents: "none",
        }}
      />
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          width: "100%",
          maxWidth: "880px",
          minHeight: { md: "560px" },
          borderRadius: "16px",
          overflow: "hidden",
          boxShadow: "0 30px 80px rgba(4, 16, 33, 0.45)",
          position: "relative",
          zIndex: 1,
        }}
      >
        {/* Left: Brand Panel */}
        <Box
          sx={{
            flex: "1 1 45%",
            background: `linear-gradient(160deg, ${PANEL_BLUE_START} 0%, ${PANEL_BLUE_END} 100%)`,
            color: "#fff",
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            padding: { xs: "32px 28px", md: "48px 40px" },
            position: "relative",
            minHeight: { xs: "260px", md: "auto" },
          }}
        >
          {/* Decorative background shapes */}
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='340' height='340'%3E%3Cg stroke='rgba(255,255,255,0.10)' stroke-width='2' fill='none' stroke-linecap='round' stroke-linejoin='round'%3E%3Cg transform='translate(24,30) rotate(-8)'%3E%3Crect x='0' y='0' width='40' height='26' rx='2'/%3E%3Crect x='-5' y='26' width='50' height='5' rx='2'/%3E%3C/g%3E%3Cg transform='translate(195,42) rotate(14)'%3E%3Crect x='0' y='0' width='20' height='36' rx='4'/%3E%3Cline x1='8' y1='31' x2='12' y2='31'/%3E%3C/g%3E%3Cg transform='translate(60,160) rotate(-15)' stroke='rgba(200,155,60,0.20)'%3E%3Crect x='0' y='0' width='46' height='29' rx='5'/%3E%3Cline x1='0' y1='10' x2='46' y2='10'/%3E%3Cline x1='7' y1='20' x2='22' y2='20'/%3E%3C/g%3E%3Cg transform='translate(230,195)'%3E%3Cpath d='M0,17 A17,17 0 0,1 34,17'/%3E%3Crect x='-3' y='14' width='7' height='12' rx='3'/%3E%3Crect x='30' y='14' width='7' height='12' rx='3'/%3E%3C/g%3E%3Cg transform='translate(120,255) rotate(6)'%3E%3Crect x='0' y='7' width='40' height='27' rx='3'/%3E%3Cpath d='M13,7 v-5 a3,3 0 0 1 3,-3 h7 a3,3 0 0 1 3,3 v5'/%3E%3Cline x1='0' y1='19' x2='40' y2='19'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E\")",
              backgroundRepeat: "repeat",
              backgroundSize: "260px 260px",
              pointerEvents: "none",
            }}
          />
          <Box
            sx={{
              position: "absolute",
              top: "-80px",
              right: "-80px",
              width: "260px",
              height: "260px",
              borderRadius: "50%",
              background: `radial-gradient(circle, ${GOLD}26 0%, transparent 70%)`,
              pointerEvents: "none",
            }}
          />
          <Box
            sx={{
              position: "absolute",
              bottom: "10%",
              left: "-100px",
              width: "220px",
              height: "220px",
              borderRadius: "50%",
              border: "1px solid rgba(255,255,255,0.08)",
              pointerEvents: "none",
            }}
          />

          <Box
            sx={{
              background: "rgba(255,255,255,0.95)",
              borderRadius: "12px",
              padding: "14px 22px",
              display: "inline-flex",
              alignItems: "center",
              alignSelf: "center",
              position: "relative",
              zIndex: 1,
            }}
          >
            <img src={Logo} alt="SriFin Credit Private Limited" style={{ display: "block", height: "62px", width: "auto" }} />
          </Box>
          <Typography
            sx={{
              textAlign: "center",
              fontWeight: 700,
              fontSize: "28px",
              letterSpacing: "5px",
              color: "#fff",
              mt: 2,
              position: "relative",
              zIndex: 1,
            }}
          >
            AMS
          </Typography>
          <Box
            sx={{
              width: "40px",
              height: "3px",
              background: GOLD,
              borderRadius: "2px",
              alignSelf: "center",
              mt: 1.5,
              position: "relative",
              zIndex: 1,
            }}
          />

          <Box sx={{ my: { xs: 2, md: 0 }, display: "flex", gap: "10px", flexWrap: "wrap", justifyContent: "center", position: "relative", zIndex: 1 }}>
            {["Assets", "Call track", "Debit cards"].map((label) => (
              <Box
                key={label}
                sx={{
                  border: `1px solid ${GOLD}88`,
                  borderRadius: "20px",
                  padding: "6px 16px",
                  fontSize: "12px",
                  fontWeight: 600,
                  color: "#111111",
                  background: "rgba(255,255,255,0.85)",
                  letterSpacing: "0.3px",
                }}
              >
                {label}
              </Box>
            ))}
          </Box>

          <Box sx={{ position: "relative", zIndex: 1, textAlign: "center" }}>
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: { xs: "22px", md: "25px" },
                lineHeight: 1.3,
                mb: 1,
              }}
            >
              One platform for asset,<br />call and card operations
            </Typography>
            <Typography sx={{ fontSize: "13px", color: "rgba(255,255,255,0.65)", maxWidth: "320px", mx: "auto" }}>
              Sign in to manage assignments, track calls and process debit card requests.
            </Typography>
          </Box>
        </Box>

        {/* Right: Login Form */}
        <Box
          component="form"
          onSubmit={handleLogin}
          sx={{
            flex: "1 1 55%",
            background: "linear-gradient(165deg, #FFFFFF 0%, #F4F7FB 60%, #EEF2F8 100%)",
            padding: { xs: "32px 28px", md: "44px 44px" },
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            position: "relative",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              top: "-60px",
              right: "-60px",
              width: "180px",
              height: "180px",
              borderRadius: "50%",
              background: `radial-gradient(circle, ${GOLD}22 0%, transparent 70%)`,
              pointerEvents: "none",
            }}
          />
          <Box
            sx={{
              position: "absolute",
              bottom: "-40px",
              left: "-40px",
              width: "140px",
              height: "140px",
              borderRadius: "50%",
              background: `radial-gradient(circle, ${SLATE}14 0%, transparent 70%)`,
              pointerEvents: "none",
            }}
          />
          <Box sx={{ position: "relative", zIndex: 1 }}>
          <Typography sx={{ fontSize: "13px", fontWeight: 600, color: GOLD, letterSpacing: "1px", mb: 0.5 }}>
            WELCOME BACK
          </Typography>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              fontSize: "26px",
              color: NAVY,
              mb: 3.5,
            }}
          >
            Sign in to your account
          </Typography>

          <TextField
            label="Employee ID"
            variant="outlined"
            fullWidth
            value={emp_id}
            onChange={(e) => setemp_id(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <AccountCircleIcon sx={{ color: SLATE }} />
                </InputAdornment>
              ),
            }}
            sx={{
              mb: 2.5,
              "& .MuiOutlinedInput-root": {
                borderRadius: "10px",
                "&.Mui-focused fieldset": { borderColor: SLATE },
              },
              "& .MuiInputLabel-root.Mui-focused": { color: SLATE },
            }}
          />

          <TextField
            label="Password"
            type={showPassword ? "text" : "password"}
            variant="outlined"
            fullWidth
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockIcon sx={{ color: SLATE }} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={togglePasswordVisibility} edge="end" aria-label="toggle password visibility">
                    {showPassword ? <VisibilityOff sx={{ color: SLATE }} /> : <Visibility sx={{ color: SLATE }} />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              mb: 1.5,
              "& .MuiOutlinedInput-root": {
                borderRadius: "10px",
                "&.Mui-focused fieldset": { borderColor: SLATE },
              },
              "& .MuiInputLabel-root.Mui-focused": { color: SLATE },
            }}
          />

          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={rememberMe}
                  onChange={() => setRememberMe(!rememberMe)}
                  sx={{ color: SLATE, "&.Mui-checked": { color: SLATE } }}
                />
              }
              label={<Typography sx={{ fontSize: "13px", color: "#555" }}>Remember me</Typography>}
            />
            <Link
              to="/ForgotPassword"
              style={{
                color: SLATE,
                fontSize: "13px",
                display: "flex",
                alignItems: "center",
                textDecoration: "none",
                fontWeight: 500,
              }}
            >
              <MailOutlineIcon sx={{ mr: 0.5, fontSize: "16px" }} />
              Forgot password?
            </Link>
          </Box>

          <Button
            variant="contained"
            fullWidth
            type="submit"
            disabled={loading}
            sx={{
              py: 1.3,
              textTransform: "none",
              fontSize: "15px",
              fontWeight: 600,
              borderRadius: "10px",
              backgroundColor: NAVY,
              boxShadow: "none",
              "&:hover": {
                backgroundColor: SLATE,
                boxShadow: "none",
              },
            }}
          >
            {loading ? <CircularProgress size={22} color="inherit" /> : "Sign in"}
          </Button>

          {message && (
            <Typography
              sx={{
                mt: 2,
                fontSize: "13px",
                color: message.toLowerCase().includes("successful") ? "#1D9E75" : "#D14343",
                textAlign: "center",
              }}
            >
              {message}
            </Typography>
          )}
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: "6px", justifyContent: "center", pt: 4, position: "relative", zIndex: 1 }}>
            <LockIcon sx={{ fontSize: "13px", color: "#9AA5B1" }} />
            <Typography sx={{ fontSize: "12px", color: "#9AA5B1" }}>
              Your connection to this portal is secured
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default Login;